/**
 * BranchOutcomes - the body of S-BRANCH (UX-RW-03 v1.1 S-BRANCH; RW-01 S1
 * terminus, S7/S8 by reference; TL ruling Q1). One section per state of the
 * branch lookup; every state names itself, nothing reads as zero activity:
 *
 *  - looking for the row (first load, or walking pages on a cold deep link):
 *    a loading line;
 *  - the lookup failed (first page) or stalled (a later page): the named
 *    failure with Retry - a stalled walk resumes where it stopped;
 *  - not found after the whole list was read: the unknown-branch state with an
 *    escape back to the overview - never zeros, never a fake row;
 *  - the row is unreachable: the verbatim reason, no counts, no worklists;
 *  - the row answered with no invoices: "No invoices in period" + the scope;
 *  - the row answered: compact KPI row (server counts) and the two worklists.
 */

import type { PeriodSelection } from '@domain/types'
import type { ReachableBranch } from '@domain/tpin'
import { NO_KPI_LINKS, STATUS_WORD, scopeText } from '@domain/tpinDash'
import { hasNoInvoices, type BranchLookup } from '@domain/tpinBranch'
import { failedListPath, pendingListPath, tpinPath } from '@domain/tpinPaths'
import { KpiCards } from './KpiTrio'
import { Link } from './Link'
import { EmptyState, ErrorState, LoadingState, UnreachableState } from './RealStates'

function Worklists({ row, period }: { readonly row: ReachableBranch; readonly period: PeriodSelection }) {
  const entries = [
    { key: 'failed', to: failedListPath(row.dbName), count: row.failed, label: 'Failed invoices' },
    { key: 'pending', to: pendingListPath(row.dbName), count: row.pending, label: 'Pending invoices' },
  ] as const
  return (
    <section className="branch-worklists" aria-labelledby="worklists-heading" data-testid="branch-worklists">
      <h2 id="worklists-heading" className="dash-heading">
        Worklists
      </h2>
      <ul className="worklist" data-testid="worklist">
        {entries.map((entry) => (
          <li key={entry.key} className="worklist-item">
            <Link
              to={entry.to}
              period={period}
              className="worklist-link"
              testid={`worklist-${entry.key}`}
              ariaLabel={`${STATUS_WORD[entry.key]} — ${entry.count} invoices for branch ${row.dbName}`}
            >
              <span className="worklist-label">{entry.label}</span>
              <span className="worklist-count">{entry.count}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Body({
  lookup,
  tin,
  dbName,
  from,
  to,
  period,
  onRetry,
  onRetryWalk,
}: {
  readonly lookup: BranchLookup
  readonly tin: string
  readonly dbName: string
  readonly from: string
  readonly to: string
  readonly period: PeriodSelection
  readonly onRetry: () => void
  readonly onRetryWalk: () => void
}) {
  switch (lookup.kind) {
    case 'error':
      return (
        <div data-testid="branch-error">
          <ErrorState message={lookup.message} onRetry={onRetry} />
        </div>
      )
    case 'stalled':
      return (
        <div data-testid="branch-stalled">
          <ErrorState message={lookup.message} onRetry={onRetryWalk} />
        </div>
      )
    case 'not-found':
      return (
        <div role="alert" className="state state-error branch-notfound" data-testid="branch-not-found">
          <p className="branch-notfound-title">{`This branch was not found for TPIN ${tin}.`}</p>
          <p>Check the branch identifier, or go back to the overview to choose from the full branch list.</p>
          <p>
            <Link to={tpinPath(tin)} period={period} className="dash-hero-change" testid="branch-notfound-back">
              Back to TPIN overview
            </Link>
          </p>
        </div>
      )
    case 'found':
      if (lookup.row.kind === 'unreachable') {
        return <UnreachableState dbName={lookup.row.dbName} reason={lookup.row.reason} />
      }
      if (hasNoInvoices(lookup.row)) {
        return <EmptyState scope={`${dbName} — ${scopeText(from, to)}`} />
      }
      return (
        <>
          <p className="page-scope" data-testid="branch-scope">
            {scopeText(from, to)}
          </p>
          <KpiCards
            counts={lookup.row}
            label="Branch sync outcomes"
            links={NO_KPI_LINKS}
            period={period}
          />
          <Worklists row={lookup.row} period={period} />
        </>
      )
    default:
      return <LoadingState label={`Loading branch ${dbName}…`} />
  }
}

export function BranchOutcomes(props: {
  readonly lookup: BranchLookup
  readonly tin: string
  readonly dbName: string
  readonly from: string
  readonly to: string
  readonly period: PeriodSelection
  /** Retry after the first page failed. */
  readonly onRetry: () => void
  /** Resume a walk that stopped on a failed page. */
  readonly onRetryWalk: () => void
}) {
  return (
    <section className="dash-outcomes" aria-labelledby="branch-outcomes-heading" data-testid="branch-outcomes">
      <h2 id="branch-outcomes-heading" className="dash-heading">
        Branch outcomes
      </h2>
      <Body {...props} />
    </section>
  )
}
